import { useState, useEffect, useMemo } from 'react'
import { Head } from '@inertiajs/react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  ChevronDown,
  ChevronRight,
  Search,
  Database,
  Key,
  Link2,
  Table2,
} from 'lucide-react'

// Table category configuration
const TABLE_CATEGORIES = {
  application: { label: 'Application', icon: Table2 },
  rodauth: { label: 'Rodauth (Auth)', icon: Key },
  rails_internal: { label: 'Rails Internal', icon: Database },
}

// Column type colors
function getTypeColor(type) {
  const colors = {
    integer: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    bigint: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    string: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    text: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    citext: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    boolean: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    datetime: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    date: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    json: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
    jsonb: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
  }
  return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
}

// Columns Table Component
function ColumnsTable({ columns, primaryKey }) {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-muted-foreground">
        Columns
      </h4>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Type</th>
              <th className="text-left px-3 py-2 font-medium">Nullable</th>
              <th className="text-left px-3 py-2 font-medium">Default</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {columns.map((col) => (
              <tr key={col.name} className="hover:bg-muted/50">
                <td className="px-3 py-2 font-mono text-xs">
                  {col.name}
                  {col.name === primaryKey && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-1.5 py-0.5 rounded">
                      PK
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <code className={`text-xs px-1.5 py-0.5 rounded ${getTypeColor(col.type)}`}>
                    {col.sql_type}
                  </code>
                </td>
                <td className="px-3 py-2">
                  {col.nullable ? (
                    <span className="text-muted-foreground text-xs">NULL</span>
                  ) : (
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">NOT NULL</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground max-w-[200px] truncate">
                  {col.default || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Indexes List Component
function IndexesList({ indexes }) {
  if (indexes.length === 0) return null

  return (
    <div>
      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-muted-foreground">
        <Key className="h-4 w-4" />
        Indexes
      </h4>
      <div className="space-y-2">
        {indexes.map((idx) => (
          <div key={idx.name} className="border rounded-lg p-3 bg-muted/30">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs font-semibold break-all">{idx.name}</code>
              {idx.unique && (
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-1.5 py-0.5 rounded shrink-0">
                  UNIQUE
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              <code>{idx.columns.join(', ')}</code>
              {idx.where && (
                <span className="ml-2 block mt-1">
                  WHERE: <code>{idx.where}</code>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Foreign Keys List Component
function ForeignKeysList({ outgoing, incoming }) {
  if (outgoing.length === 0 && incoming.length === 0) return null

  return (
    <div>
      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-muted-foreground">
        <Link2 className="h-4 w-4" />
        Foreign Keys
      </h4>
      <div className="space-y-2">
        {outgoing.map((fk) => (
          <div key={fk.name} className="border rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30">
            <div className="text-xs">
              <span className="font-mono">{fk.column}</span>
              <span className="mx-2 text-muted-foreground">→</span>
              <a
                href={`#table-${fk.to_table}`}
                className="font-mono text-blue-600 dark:text-blue-400 hover:underline"
              >
                {fk.to_table}.{fk.primary_key}
              </a>
              {fk.on_delete && (
                <span className="ml-2 text-muted-foreground">
                  ON DELETE {fk.on_delete.toUpperCase()}
                </span>
              )}
            </div>
          </div>
        ))}
        {incoming.map((fk) => (
          <div key={fk.name} className="border rounded-lg p-3 bg-green-50 dark:bg-green-950/30">
            <div className="text-xs">
              <span className="text-muted-foreground">Referenced by: </span>
              <a
                href={`#table-${fk.from_table}`}
                className="font-mono text-green-600 dark:text-green-400 hover:underline"
              >
                {fk.from_table}.{fk.column}
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Table Card Component
function TableCard({ table, foreignKeys }) {
  const tableFKs = foreignKeys.filter((fk) => fk.from_table === table.name)
  const referencedBy = foreignKeys.filter((fk) => fk.to_table === table.name)

  return (
    <Card id={`table-${table.name}`} className="scroll-mt-20">
      <CardHeader className="border-b pb-4">
        <div className="flex items-center gap-2">
          <Table2 className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="font-mono text-lg">{table.name}</CardTitle>
        </div>
        <CardDescription>
          {table.columns.length} columns
          {table.indexes.length > 0 && `, ${table.indexes.length} indexes`}
          {tableFKs.length > 0 && `, ${tableFKs.length} FK`}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4 space-y-6">
        <ColumnsTable columns={table.columns} primaryKey={table.primary_key} />
        <IndexesList indexes={table.indexes} />
        <ForeignKeysList outgoing={tableFKs} incoming={referencedBy} />
      </CardContent>
    </Card>
  )
}

export default function Schema({ railsEnv, appName, tables = [], foreignKeys = [], schemaVersion }) {
  const [isDark, setIsDark] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTable, setActiveTable] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState({
    application: true,
    rodauth: false,
    rails_internal: false,
  })

  // Check initial dark mode state
  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'))
  }, [])

  // Dark mode toggle
  const toggleDarkMode = () => {
    const newValue = !isDark
    setIsDark(newValue)
    document.documentElement.classList.toggle('dark', newValue)
  }

  // Filter tables based on search
  const filteredTables = useMemo(() => {
    if (!searchQuery) return tables
    const query = searchQuery.toLowerCase()
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.columns.some((c) => c.name.toLowerCase().includes(query))
    )
  }, [tables, searchQuery])

  // Group tables by category
  const tablesByCategory = useMemo(() => {
    return filteredTables.reduce((acc, table) => {
      const category = table.category || 'application'
      if (!acc[category]) acc[category] = []
      acc[category].push(table)
      return acc
    }, {})
  }, [filteredTables])

  // Toggle category expansion
  const toggleCategory = (key) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  // Intersection observer for active table highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace('table-', '')
            setActiveTable(id)
          }
        })
      },
      { rootMargin: '-20% 0px -70% 0px' }
    )

    tables.forEach(({ name }) => {
      const el = document.getElementById(`table-${name}`)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [tables])

  return (
    <>
      <Head title="Database Schema" />

      <div className="min-h-screen bg-background">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
          <div className="container flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">{appName} Schema</h1>
              <span className="px-2 py-1 text-xs rounded-full bg-primary text-primary-foreground">
                {railsEnv}
              </span>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                v{schemaVersion}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/dev/style-guide"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Style Guide
              </a>
              <Button variant="outline" size="sm" onClick={toggleDarkMode}>
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </Button>
            </div>
          </div>
        </header>

        <div className="container flex gap-8 py-8">
          {/* Sticky Sidebar Navigation */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="sticky top-20 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tables..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Stats */}
              <div className="text-xs text-muted-foreground px-2">
                {filteredTables.length} of {tables.length} tables
              </div>

              {/* Category Groups */}
              {Object.entries(TABLE_CATEGORIES).map(([key, config]) => {
                const categoryTables = tablesByCategory[key] || []
                if (categoryTables.length === 0 && !searchQuery) return null
                if (categoryTables.length === 0) return null

                const Icon = config.icon

                return (
                  <div key={key}>
                    <button
                      onClick={() => toggleCategory(key)}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm font-medium hover:bg-accent/50 rounded"
                    >
                      {expandedCategories[key] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1">{config.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {categoryTables.length}
                      </span>
                    </button>

                    {expandedCategories[key] && (
                      <nav className="ml-6 mt-1 space-y-0.5">
                        {categoryTables.map((table) => (
                          <a
                            key={table.name}
                            href={`#table-${table.name}`}
                            className={`block px-2 py-1 text-xs font-mono rounded truncate ${
                              activeTable === table.name
                                ? 'bg-accent text-accent-foreground'
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                            }`}
                          >
                            {table.name}
                          </a>
                        ))}
                      </nav>
                    )}
                  </div>
                )
              })}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 space-y-6">
            {/* Mobile Search */}
            <div className="lg:hidden relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tables..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {filteredTables.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No tables found matching "{searchQuery}"
              </div>
            ) : (
              filteredTables.map((table) => (
                <TableCard key={table.name} table={table} foreignKeys={foreignKeys} />
              ))
            )}
          </main>
        </div>
      </div>
    </>
  )
}
